// Iter80 — Read-only Orders page.
// Iter82 — Expanded line-item rendering with images, vendor source,
// shipping address, Square IDs. Reusable across /admin/orders,
// /partner/orders, /franchise/orders.
import React, { useEffect, useState } from "react";
import { Loader2, Search, ChevronDown, ChevronRight, Wrench } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ProductImg } from "@/lib/productImage";

const SCOPE_ENDPOINTS = {
  admin:     { path: "/admin/orders",     title: "All Orders" },
  partner:   { path: "/partner/orders",   title: "Your Orders" },
  franchise: { path: "/franchise/orders", title: "Franchise Orders" },
};

const STATUS_OPTIONS = [
  { v: "",            label: "All statuses" },
  { v: "pending",     label: "Pending" },
  { v: "in_transit",  label: "In Transit" },
  { v: "delivered",   label: "Delivered" },
  { v: "cancelled",   label: "Cancelled" },
];

const VENDOR_LABEL = {
  admin:           "LATM",
  lifted:          "LATM",
  lifted_admin:    "LATM",
  partner:         "Partner",
  third_party_partner: "Partner",
  franchise:       "Franchise",
  franchisee:      "Franchise",
  lifted_supplies: "Lifted Supplies",
  same_day:        "Lifted Supplies",
  unknown:         "Unknown",
};

function money(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function formatAddress(addr) {
  if (!addr) return "—";
  const lines = [
    addr.name,
    [addr.line1, addr.line2].filter(Boolean).join(", "),
    [addr.city, addr.state, addr.zip || addr.postal_code].filter(Boolean).join(", "),
    addr.country,
  ].filter(Boolean);
  return lines.join(" · ");
}

export default function OrdersPage({ scope = "admin" }) {
  const cfg = SCOPE_ENDPOINTS[scope] || SCOPE_ENDPOINTS.admin;
  const [items, setItems]   = useState([]);
  const [page, setPage]     = useState(1);
  const [total, setTotal]   = useState(0);
  const [pages, setPages]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState("");
  const [ok, setOk]         = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState({});
  const [backfilling, setBackfilling] = useState({});

  const limit = 30;
  const isAdmin = scope === "admin";

  async function reload(p = page) {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(cfg.path, {
        params: { page: p, limit, search, status },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.total_pages || 0);
      setPage(data.page || p);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(1); /* eslint-disable-next-line */ }, [scope, search, status]);

  const toggle = (id) => {
    setExpanded((m) => {
      const wasOpen = !!m[id];
      const next = { ...m, [id]: !wasOpen };
      // Iter82 polish — when an admin expands an order that still has
      // coded references (no title or unresolved=false), auto-backfill
      // so the row never shows a raw placement handle as the title.
      if (!wasOpen && isAdmin) {
        const row = items.find((r) => r.id === id);
        const lineItems = row?.items || [];
        const looksCoded = lineItems.some(
          (li) => li && (li.resolved === false || (!li.title && li.handle)),
        );
        if (looksCoded && !backfilling[id]) {
          // fire-and-forget; backfillRow updates items[] in place.
          backfillRow(id);
        }
      }
      return next;
    });
  };

  async function backfillRow(orderId) {
    setBackfilling((b) => ({ ...b, [orderId]: true }));
    setErr(""); setOk("");
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/backfill`);
      setOk(`Backfilled ${orderId}: ${data.resolved_count}/${(data.items || []).length} items resolved.`);
      // Patch the row in place so the user sees the enriched items immediately.
      setItems((rows) => rows.map((r) => r.id === orderId ? { ...r, items: data.items } : r));
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBackfilling((b) => ({ ...b, [orderId]: false }));
    }
  }

  async function backfillRecent() {
    setOk(""); setErr("");
    try {
      const { data } = await api.post("/admin/orders/backfill-recent?limit=4");
      setOk(`Backfilled ${data.processed} recent orders.`);
      reload(page);
    } catch (e) {
      setErr(apiErrorMessage(e));
    }
  }

  // Iter89 — Admin "Force Shopify" action (formerly "Force Square").
  // Re-creates a Shopify-hosted checkout for an existing draft so the
  // admin can recover stuck carts or hand a fresh URL to a customer.
  async function forceShopify(checkoutId) {
    if (!checkoutId) {
      setErr("This order has no checkout_id — cannot force Shopify.");
      return;
    }
    setOk(""); setErr("");
    try {
      const { data } = await api.post("/admin/shopify/checkout/force-shopify",
                                       { checkout_id: checkoutId });
      if (data?.ok && data?.checkout_url) {
        setOk(`Shopify checkout ready: ${data.checkout_url}`);
        window.open(data.checkout_url, "_blank", "noopener,noreferrer");
      } else if (data?.error === "variant_missing") {
        setErr(data.customer_message || "Some items lack a Shopify variant id.");
      } else {
        setErr(data?.customer_message || data?.error || "Could not force Shopify checkout.");
      }
    } catch (e) {
      setErr(apiErrorMessage(e));
    }
  }

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid={`orders-page-${scope}`}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow text-lifted-green">{scope.toUpperCase()}</div>
          <h1 className="font-anton text-4xl uppercase text-white">{cfg.title}</h1>
          <p className="text-lifted-dim text-sm mt-1">
            {total.toLocaleString()} order{total === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <button
              onClick={backfillRecent}
              className="btn-lifted-outline text-xs inline-flex items-center gap-1"
              data-testid="orders-backfill-recent"
              title="Re-resolve product snapshots on the 4 most-recent orders"
            >
              <Wrench className="h-3 w-3" /> Backfill last 4
            </button>
          )}
          <label className="relative">
            <Search className="absolute left-2 top-2 h-3 w-3 text-lifted-dim" />
            <input
              type="text"
              placeholder="Search orders…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid={`orders-search-${scope}`}
              className="pl-7 pr-3 py-1.5 rounded-md bg-black/40 border border-white/15 text-white text-sm"
            />
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid={`orders-status-${scope}`}
            className="px-2 py-1.5 rounded-md bg-black/40 border border-white/15 text-white text-sm"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.v} value={s.v}>{s.label}</option>
            ))}
          </select>
        </div>
      </header>

      {ok && <div className="border border-lifted-green/40 bg-lifted-green/10 rounded-md p-2 text-xs text-lifted-green"
                  data-testid={`orders-ok-${scope}`}>{ok}</div>}
      {err && (
        <div className="border border-rose-500/40 bg-rose-500/10 rounded-md p-3 text-sm text-rose-200"
             data-testid={`orders-error-${scope}`}>{err}</div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-lifted-dim">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-lifted-dim py-12 border border-dashed border-white/15 rounded-xl"
             data-testid={`orders-empty-${scope}`}>
          No orders match the current filters.
        </div>
      ) : (
        <div className="space-y-2" data-testid={`orders-list-${scope}`}>
          {items.map((o) => {
            const open = !!expanded[o.id];
            const lineItems = o.items || [];
            return (
              <article
                key={o.id}
                className="rounded-md border border-white/10 overflow-hidden bg-black/30"
                data-testid={`orders-row-${o.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(o.id)}
                  className="w-full text-left grid grid-cols-12 gap-2 items-center px-3 py-2 hover:bg-white/5"
                  data-testid={`orders-row-toggle-${o.id}`}
                >
                  <div className="col-span-1 text-lifted-dim">
                    {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </div>
                  <div className="col-span-2 text-white text-sm font-mono truncate" data-testid={`orders-row-${o.id}-number`}>
                    {o.order_number || (o.id || "").slice(0, 8)}
                  </div>
                  <div className="col-span-3 text-white text-sm truncate">
                    {o.customer_name || o.customer_email || "—"}
                  </div>
                  <div className="col-span-1 text-lifted-dim text-xs">
                    {lineItems.length} item{lineItems.length === 1 ? "" : "s"}
                  </div>
                  <div className="col-span-1 text-right text-lifted-green font-mono text-sm">
                    {money(o.total_cents)}
                  </div>
                  <div className="col-span-1 text-[0.65rem]">
                    <span className="px-2 py-0.5 rounded-full bg-white/10 text-white">{o.payment_status || "—"}</span>
                  </div>
                  <div className="col-span-2 text-[0.65rem]">
                    <span className="px-2 py-0.5 rounded-full bg-white/10 text-white">{o.fulfillment_status || "—"}</span>
                  </div>
                  <div className="col-span-1 text-lifted-dim text-[0.65rem] truncate">
                    {(o.created_at || "").slice(0, 16)}
                  </div>
                </button>

                {open && (
                  <div className="px-4 py-4 border-t border-white/10 grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs"
                       data-testid={`orders-row-${o.id}-detail`}>
                    {/* Line items */}
                    <section className="lg:col-span-2 space-y-2" data-testid={`orders-row-${o.id}-items`}>
                      <div className="eyebrow text-lifted-green">Line Items</div>
                      {lineItems.length === 0 ? (
                        <div className="text-lifted-dim">No items.</div>
                      ) : (
                        <ul className="space-y-2">
                          {lineItems.map((li, idx) => (
                            <li
                              key={idx}
                              className="flex items-center gap-3 p-2 rounded border border-white/10 bg-black/30"
                              data-testid={`orders-row-${o.id}-item-${idx}`}
                            >
                              <ProductImg
                                product={li}
                                alt={li.title}
                                className="h-12 w-12 rounded object-cover bg-white/5"
                                data-testid={`orders-row-${o.id}-item-${idx}-img`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-white truncate" data-testid={`orders-row-${o.id}-item-${idx}-title`}>
                                  {li.title || li.handle || "Unknown item"}
                                  {li.resolved === false && (
                                    <span className="ml-2 text-amber-300 text-[0.65rem]">⚠ coded ref — backfill needed</span>
                                  )}
                                </div>
                                <div className="text-lifted-dim text-[0.65rem] truncate">
                                  {li.sku && <>SKU: {li.sku} · </>}
                                  Qty: {li.quantity || 1} · {money(li.unit_price_cents)}
                                </div>
                                <div className="text-[0.65rem]">
                                  <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-200 mr-1">
                                    {VENDOR_LABEL[li.vendor_source] || li.vendor_source || "Admin"}
                                  </span>
                                  {li.vendor_name && (
                                    <span className="text-lifted-dim">· {li.vendor_name}</span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right text-lifted-green font-mono text-sm">
                                {money((li.unit_price_cents || 0) * (li.quantity || 1))}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                      {isAdmin && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={(e) => { e.preventDefault(); backfillRow(o.id); }}
                            disabled={!!backfilling[o.id]}
                            className="btn-lifted-outline text-[0.65rem] inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid={`orders-backfill-${o.id}`}
                          >
                            <Wrench className="h-3 w-3" />
                            {backfilling[o.id] ? "Backfilling…" : "Re-resolve line items"}
                          </button>
                          <button
                            onClick={(e) => { e.preventDefault(); forceShopify(o.checkout_id); }}
                            disabled={!o.checkout_id}
                            className="btn-lifted-outline text-[0.65rem] inline-flex items-center gap-1 disabled:opacity-40"
                            data-testid={`force-shopify-btn-${o.id}`}
                            title={o.checkout_id
                              ? "Re-create a Shopify-hosted checkout URL for this draft"
                              : "No checkout draft is attached to this order — cannot force Shopify"}
                          >
                            Force Shopify
                          </button>
                        </div>
                      )}
                    </section>

                    {/* Customer + shipping + Square ids */}
                    <aside className="space-y-3" data-testid={`orders-row-${o.id}-aside`}>
                      <div>
                        <div className="eyebrow text-lifted-green">Customer</div>
                        <div className="text-white">{o.customer_name || "—"}</div>
                        <div className="text-lifted-dim">{o.customer_email || "—"}</div>
                        <div className="text-lifted-dim">{o.customer_phone || "—"}</div>
                      </div>
                      <div>
                        <div className="eyebrow text-lifted-green">Shipping</div>
                        <div className="text-white whitespace-pre-line" data-testid={`orders-row-${o.id}-shipping`}>
                          {formatAddress(o.shipping_address)}
                        </div>
                      </div>
                      <div>
                        <div className="eyebrow text-lifted-green">Payment</div>
                        {o.processor === "shopify" || o.shopify_order_id || o.shopify_cart_id ? (
                          <>
                            <div className="text-lifted-dim font-mono break-all" data-testid={`orders-row-${o.id}-shopify-order`}>
                              Shopify order: {o.shopify_order_number || o.shopify_order_id || "—"}
                            </div>
                            <div className="text-lifted-dim font-mono break-all">
                              Cart: {(o.shopify_cart_id || "").slice(0, 32) || "—"}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-lifted-dim font-mono break-all">
                              Square pay: {o.square_payment_id || o.id?.slice(0, 8) || "—"}
                            </div>
                            <div className="text-lifted-dim font-mono break-all">
                              Square order: {o.square_order_id || "—"}
                            </div>
                          </>
                        )}
                        <div className="text-lifted-dim">Method: {o.fulfillment_method || "—"}</div>
                      </div>
                    </aside>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2"
             data-testid={`orders-pagination-${scope}`}>
          <button
            disabled={page <= 1}
            onClick={() => reload(page - 1)}
            data-testid={`orders-prev-${scope}`}
            className="btn-lifted-outline text-xs disabled:opacity-40"
          >
            Prev
          </button>
          <span className="text-xs text-lifted-dim">
            Page <span className="text-white">{page}</span> of {pages}
          </span>
          <button
            disabled={page >= pages}
            onClick={() => reload(page + 1)}
            data-testid={`orders-next-${scope}`}
            className="btn-lifted-outline text-xs disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
